import { scopedStorageKey } from './deployment.js';
import { normalizarHabilidadeId } from './skillAliases.js';

export const PEDAGOGICAL_ALERT_DISMISSALS_KEY = scopedStorageKey('sgaee.pedagogicalAlertDismissals.v2');

const DAY_MS = 86400000;
const SEVERITY_ORDER = { seguranca: 0, atencao: 1, revisao: 2 };

function asList(value) { return Array.isArray(value) ? value.filter(Boolean) : value ? [value] : []; }
function studentIdOf(value = {}) { return value.studentId || value.alunoId || value.id || ''; }
function dateOf(value) { const date = value ? new Date(value) : null; return date && !Number.isNaN(date.getTime()) ? date : null; }
function dateText(value = {}) { return value.data || value.date || value.createdAt || ''; }
function normalizeText(value = '') { return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(); }
function scoreOf(item = {}) {
  const score = Number(item.score);
  if (Number.isFinite(score)) return score <= 1 ? score * 100 : score;
  return { conseguiu: 100, parcial: 60, nao_conseguiu: 0, 'não conseguiu': 0 }[normalizeText(item.resultado || item.desempenho)] ?? null;
}
function itemKey(item = {}) { return item.atividadeId || item.activityId || asList(item.habilidades || item.habilidadesAlvo)[0] || 'atividade'; }
function itemTitle(item = {}) { return item.titulo || item.tituloSnapshot || item.atividadeTitulo || 'atividade acompanhada'; }
function supportOf(item = {}) { return normalizeText(item.apoio || item.nivelApoio).replaceAll(' ', '_'); }
function generalizationOf(item = {}) { return asList(item.generalizacao || item.generalization); }
function logText(log = {}, item = {}) { return normalizeText([log.observacao, log.estrategia, item.motivoInterrupcao, item.obs, item.contexto, item.adaptacao].filter(Boolean).join(' ')); }
function activeMeta(meta = {}) { return !['concluida', 'concluido', 'concluído', 'cancelada'].includes(normalizeText(meta.status)); }
function skillIds(meta = {}) { return asList(meta.habilidades || meta.habilidade_alvo || meta.habilidade).map(normalizarHabilidadeId).filter(Boolean); }
function logSkills(log = {}) { return asList(log.habilidades).concat((log.itens || []).flatMap((item) => item.habilidades || item.habilidadesAlvo || [])).map(normalizarHabilidadeId).filter(Boolean); }
function daysSince(value, today) { const date = dateOf(value); return date ? Math.max(0, Math.floor((today.getTime() - date.getTime()) / DAY_MS)) : null; }

function flattenLogs(logs = []) {
  return logs.flatMap((log) => {
    const itens = Array.isArray(log.itens) && log.itens.length ? log.itens : [{ atividadeId: log.atividadeId, titulo: log.objetivo, obs: log.observacao }];
    return itens.map((item) => ({ log, item, date: dateText(log), text: logText(log, item) }));
  }).sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function makeAlert({ student, type, subject = '', severity = 'revisao', cause, action, evidence = '' }) {
  const studentId = studentIdOf(student);
  return {
    id: [studentId, type, subject].filter(Boolean).join(':'),
    studentId,
    studentName: student.nome || student.name || studentId,
    type,
    severity,
    cause,
    action,
    signature: normalizeText(`${evidence}|${cause}`).slice(0, 420),
  };
}

function safetyAlert(student, entries) {
  const explicit = entries.filter(({ text }) => /\b(dor|risco|queda|lesao|machuc|mal-estar|crise)\b/.test(text));
  const interrupted = entries.filter(({ item }) => String(item.motivoInterrupcao || '').trim());
  if (!explicit.length && interrupted.length < 2) return null;
  const latest = [...explicit, ...interrupted].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
  const cause = explicit.length
    ? `${explicit.length} registro(s) mencionam dor, risco ou outra condição de segurança; o mais recente é de ${latest?.date || 'data não informada'}.`
    : `${interrupted.length} atividades foram interrompidas; a mais recente é de ${latest?.date || 'data não informada'}.`;
  return makeAlert({ student, type: 'seguranca_interrupcao', severity: 'seguranca', cause, action: 'Antes de repetir, verifique segurança, acessibilidade e sinais do estudante; ajuste ou suspenda a atividade quando necessário.', evidence: `${explicit.length}|${interrupted.length}|${latest?.date}` });
}

function resourceAlert(student, entries) {
  const unavailable = entries.filter(({ text }) => /\b(sem prancha|sem caa|caa indisponivel|recurso indisponivel|recurso ausente|acessibilidade indisponivel|comunicacao indisponivel|faltou recurso)\b/.test(text));
  if (!unavailable.length) return null;
  const latest = unavailable.at(-1);
  return makeAlert({ student, type: 'recurso_indisponivel', severity: 'seguranca', cause: `Há registro de recurso de comunicação ou acessibilidade indisponível em ${latest.date || 'data não informada'}.`, action: 'Garanta o recurso antes do próximo atendimento e ofereça uma alternativa acessível sem retirar a forma habitual de comunicação.', evidence: `${unavailable.length}|${latest.date}` });
}

function groupedByActivity(entries) {
  const groups = new Map();
  entries.forEach((entry) => { const key = itemKey(entry.item); groups.set(key, [...(groups.get(key) || []), entry]); });
  return groups;
}

function intenseSupportAlerts(student, entries) {
  const alerts = [];
  for (const [key, group] of groupedByActivity(entries)) {
    const measured = group.filter(({ item }) => supportOf(item));
    const recent = measured.slice(-3);
    if (recent.length === 3 && recent.every(({ item }) => supportOf(item) === 'ajuda_intensa')) {
      alerts.push(makeAlert({ student, type: 'apoio_intenso_persistente', subject: key, severity: 'atencao', cause: `${itemTitle(recent.at(-1).item)} exigiu ajuda intensa nos 3 registros mais recentes.`, action: 'Revise a acessibilidade, a etapa da tarefa e o tipo de apoio. Teste apoio menos intrusivo somente quando houver segurança e condições para participação.', evidence: recent.map(({ date }) => date).join('|') }));
    }
  }
  return alerts;
}

function noProgressAlerts(student, entries) {
  const alerts = [];
  for (const [key, group] of groupedByActivity(entries)) {
    const scored = group.map((entry) => ({ ...entry, score: scoreOf(entry.item) })).filter(({ score }) => score !== null);
    const recent = scored.slice(-3);
    if (recent.length !== 3) continue;
    const first = recent[0].score;
    const noProgress = recent.at(-1).score <= first && Math.max(...recent.map(({ score }) => score)) <= first + 5;
    if (noProgress) alerts.push(makeAlert({ student, type: 'repeticao_sem_progresso', subject: key, severity: 'atencao', cause: `${itemTitle(recent.at(-1).item)} foi registrada 3 vezes sem avanço no desempenho.`, action: 'Revise a hipótese pedagógica, varie material ou estratégia e confirme se a atividade continua ligada à meta atual.', evidence: recent.map(({ date, score }) => `${date}:${score}`).join('|') }));
  }
  return alerts;
}

function highWithoutGeneralizationAlerts(student, entries) {
  const alerts = [];
  for (const [key, group] of groupedByActivity(entries)) {
    const high = group.map((entry) => ({ ...entry, score: scoreOf(entry.item) })).filter(({ score }) => score !== null).slice(-2);
    if (high.length === 2 && high.every(({ score }) => score >= 80) && group.every(({ item }) => !generalizationOf(item).length)) {
      alerts.push(makeAlert({ student, type: 'alto_sem_generalizacao', subject: key, severity: 'revisao', cause: `${itemTitle(high.at(-1).item)} teve desempenho alto em 2 registros, mas ainda sem contexto de generalização.`, action: 'Observe a mesma habilidade com outro material, pessoa ou ambiente antes de considerá-la generalizada.', evidence: high.map(({ date, score }) => `${date}:${score}`).join('|') }));
    }
  }
  return alerts;
}

function singleAssessmentAlert(student, assessments) {
  const observations = assessments.filter((assessment) => studentIdOf(assessment) === studentIdOf(student) && Object.values(assessment.pontuacoes || assessment.habilidades || {}).some((value) => value !== '' && value !== null && value !== undefined));
  if (observations.length !== 1) return null;
  return makeAlert({ student, type: 'avaliacao_unica', severity: 'revisao', cause: `A avaliação atual se apoia em uma única data de observação (${dateText(observations[0]) || 'data não informada'}).`, action: 'Confirme as habilidades prioritárias em outra oportunidade antes de concluir domínio ou dificuldade.', evidence: `${observations[0].id}|${dateText(observations[0])}` });
}

function staleGoalAlerts(student, logs, peis, today) {
  const pei = peis.find((item) => studentIdOf(item) === studentIdOf(student));
  if (!pei) return [];
  const studentLogs = logs.filter((log) => studentIdOf(log) === studentIdOf(student));
  return (pei.metas || []).filter(activeMeta).flatMap((meta) => {
    const targets = new Set(skillIds(meta));
    const related = studentLogs.filter((log) => !targets.size || logSkills(log).some((skill) => targets.has(skill)));
    const latest = related.map((log) => dateText(log)).filter(Boolean).sort().at(-1);
    const staleDays = latest ? daysSince(latest, today) : null;
    if (related.length && (staleDays === null || staleDays <= 30)) return [];
    const label = String(meta.descricao || meta.titulo || 'Meta ativa').slice(0, 90);
    const cause = latest ? `A meta “${label}” está há ${staleDays} dias sem atendimento relacionado.` : `A meta “${label}” ainda não possui atendimento relacionado.`;
    return [makeAlert({ student, type: 'meta_sem_atendimento', subject: meta.id || label, severity: 'atencao', cause, action: 'Vincule uma próxima sessão à meta ou revise se ela ainda representa a prioridade atual.', evidence: `${meta.id}|${latest || 'nenhum'}|${related.length}` })];
  });
}

export function buildPedagogicalAlerts({ students = [], assessments = [], logs = [], peis = [], today = new Date() } = {}) {
  const currentDay = today instanceof Date ? today : new Date(today);
  const alerts = students.flatMap((student) => {
    const studentLogs = logs.filter((log) => studentIdOf(log) === studentIdOf(student));
    const entries = flattenLogs(studentLogs);
    return [
      safetyAlert(student, entries),
      resourceAlert(student, entries),
      ...intenseSupportAlerts(student, entries),
      ...noProgressAlerts(student, entries),
      singleAssessmentAlert(student, assessments),
      ...staleGoalAlerts(student, logs, peis, currentDay),
      ...highWithoutGeneralizationAlerts(student, entries),
    ].filter(Boolean);
  });
  return alerts.sort((a, b) => (SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]) || a.studentName.localeCompare(b.studentName) || a.id.localeCompare(b.id));
}

export function loadAlertDismissals(storage = globalThis.localStorage) {
  if (!storage) return [];
  try { const parsed = JSON.parse(storage.getItem(PEDAGOGICAL_ALERT_DISMISSALS_KEY) || '[]'); return Array.isArray(parsed) ? parsed : []; }
  catch { return []; }
}

export function dismissPedagogicalAlert(alert, { justification, professionalId = '', now = new Date() } = {}, storage = globalThis.localStorage) {
  const reason = String(justification || '').trim().slice(0, 180);
  if (reason.length < 3) throw new Error('Informe uma justificativa curta.');
  const dismissal = { alertId: alert.id, signature: alert.signature, justification: reason, professionalId, dismissedAt: (now instanceof Date ? now : new Date(now)).toISOString() };
  const next = [...loadAlertDismissals(storage).filter((item) => item.alertId !== alert.id), dismissal];
  storage?.setItem(PEDAGOGICAL_ALERT_DISMISSALS_KEY, JSON.stringify(next));
  return next;
}

export function activePedagogicalAlerts(alerts = [], dismissals = []) {
  return alerts.filter((alert) => !dismissals.some((item) => item.alertId === alert.id && item.signature === alert.signature));
}
